import {RoomClientState} from '../../../../shared/scripts/RoomClientState.ts'
import {UIPopupController} from '../../../../shared/scripts/ui_popup/UIPopupController'
import {BaseGameModeData, GameModeDataController} from '@colonist/shared-types'
import {ClientUserState} from '../../../../shared/scripts/ClientUserState'
import {UIComponentItemScroll} from '../../../../shared/scripts/ui_components/item_scroll/UIComponentItemScroll'
import {ItemData} from '../../../../shared/scripts/ui_components/item_scroll/UIComponentItemScrollCell'
import {ModeSettingEnum} from '@colonist/game-base-types'
import {UserState} from '../../../../../shared/UserState'
import {clientI18n} from '../../../../shared/scripts/ClientI18n'
import {TranslationArrayKeys, TranslationObject} from '@colonist/i18n'
import {createDiv, createDivIn, createImgIn, setElementInnerHTML} from '../../../../shared/scripts/ui_utils/DomUtils'
import {socket} from '../../../../shared/scripts/EntryShared.ts'

export class UIRoomCenterSettingsModeController {
    private readonly modeSelectionDiv: HTMLDivElement
    private modeSelector: UIComponentItemScroll

    private roomState: RoomClientState

    constructor() {
        this.modeSelectionDiv = document.getElementById('room_center_game_mode_settings') as HTMLDivElement
    }

    update(newRoomState: RoomClientState) {
        const shouldUpdate = this.shouldUpdate(newRoomState)
        this.roomState = newRoomState
        if(shouldUpdate) this.updateModeSelector()
    }

    load(roomState: RoomClientState) {
        this.roomState = roomState
        if(this.modeSelector == undefined) {
            this.createModeSelector()
            return
        }
        this.updateModeSelector()
    }

    updateInteractabilityOfElements(isGuest: boolean) {
        this.modeSelector.updateInteractabilityOfElements(isGuest)
    }

    private createModeSelector() {
        const gameModeItemData = this.getModeItemData()
        this.modeSelector = new UIComponentItemScroll(this.modeSelectionDiv, gameModeItemData)
    }

    private updateModeSelector() {
        const gameModeItemData = this.getModeItemData()
        this.modeSelector.update(gameModeItemData)
    }

    private getModeItemData(): ItemData[] {
        const allGameModeData = GameModeDataController.publicGameModeDataList
        const userState = ClientUserState.userState
        const gameModeItemData: ItemData[] = []
        for(const gameModeData of allGameModeData) {
            if(gameModeData.modeSetting == ModeSettingEnum.ServerStressTestClassic4P) continue
            const isGameModeAvailable = this.isGameModeAvailable(gameModeData, userState)
            const isGameModeSelected = this.isGameModeSelected(gameModeData)
            const clickAction = () => {
                socket.roomClient.setGameMode(gameModeData.modeSetting)
            }
            const displayInfoAction = () => {
                UIRoomCenterSettingsModeController.displayGameModeInfo(gameModeData)
            }

            const itemData: ItemData = {
                name: UIRoomCenterSettingsModeController.getGameModeName(gameModeData),
                image: gameModeData.icon,
                available: isGameModeAvailable,
                selected: isGameModeSelected,
                clickAction: clickAction,
                infoAction: displayInfoAction,
                showInfoIcon: true,
            }
            gameModeItemData.push(itemData)
        }
        return gameModeItemData
    }

    private isGameModeAvailable(gameModeData: BaseGameModeData, userState: UserState | undefined): boolean {
        if(this.roomState.isTestFreeExpansionsAndMaps) return true
        if(this.roomState.isTournament) return true
        if(gameModeData.isGameModePremium() == false) return true
        if(userState?.items.hasAccessToPremiumMode(gameModeData)) return true
        return false
    }

    private isGameModeSelected(gameModeData: BaseGameModeData): boolean {
        return gameModeData.modeSetting == this.roomState.gameSetting.modeSetting
    }

    private shouldUpdate(newRoomState: RoomClientState): boolean {
        const didModeChange = this.roomState.gameSetting.modeSetting != newRoomState.gameSetting.modeSetting

        if(didModeChange) return true
        return false
    }

    private static displayGameModeInfo(gameModeData: BaseGameModeData) {
        if(gameModeData.modeSetting == ModeSettingEnum.ServerStressTestClassic4P) return
        const container = createDiv('room-map-preview-grid')
        createImgIn(container, gameModeData.icon, 'room-item-popup-image')
        const gameModeInfoBody = UIRoomCenterSettingsModeController.getGameModeInfoBody(gameModeData)
        const text = UIRoomCenterSettingsModeController.createSingleMapInfo(clientI18n.tArray(gameModeInfoBody))
        const translatedMessage: TranslationObject = {key: 'strings:colonistCommon.utils.#buffer', options: {
            value: text,
            interpolation: {
                escapeValue: false,
            },
        }}
        const parentDiv = createDivIn(container, 'room-item-popup-text')
        setElementInnerHTML(parentDiv, translatedMessage)
        const popup = UIPopupController.createSimplePopup(gameModeData.name, {key: 'strings:colonistCommon.utils.emptyString'})
        if(popup === undefined) return

        popup.body.appendChild(container)
    }

    private static createSingleMapInfo(body: string[]): string {
        let text = '<ul>'
        for(const element of body) {
            text += '<li>' + element
            text += '</li>'
        }
        text += '</ul>'
        return text
    }

    private static getGameModeInfoBody(gameModeData: BaseGameModeData): TranslationArrayKeys {
        switch(gameModeData.modeSetting) {
            case ModeSettingEnum.Classic4P: return 'strings:colonistCommon.gameModes.classic4P.body'
            case ModeSettingEnum.TutorialClassic4P:
            case ModeSettingEnum.BeginnerModeClassic4P: return 'strings:colonistCommon.gameModes.classicTutorial4P.body'
            case ModeSettingEnum.Classic56P: return 'strings:colonistCommon.gameModes.classic56P.body'
            case ModeSettingEnum.Classic78P: return 'strings:colonistCommon.gameModes.classic78P.body'
            case ModeSettingEnum.Seafarers4P: return 'strings:colonistCommon.gameModes.seafarers4P.body'
            case ModeSettingEnum.Seafarers56P: return 'strings:colonistCommon.gameModes.seafarers56P.body'
            case ModeSettingEnum.CitiesAndKnights4P: return 'strings:colonistCommon.gameModes.citiesAndKnights4P.body'
            case ModeSettingEnum.CitiesAndKnights56P: return 'strings:colonistCommon.gameModes.citiesAndKnights56P.body'
            case ModeSettingEnum.CitiesAndKnightsSeafarers4P: return 'strings:colonistCommon.gameModes.seafarersAndCitiesAndKnights4P.body'
            case ModeSettingEnum.CitiesAndKnightsSeafarers56P: return 'strings:colonistCommon.gameModes.seafarersAndCitiesAndKnights56P.body'
            case ModeSettingEnum.ServerStressTestClassic4P: return 'strings:colonistCommon.gameModes.serverStressTestClassic4P.body'
            case ModeSettingEnum.ColonistRush4P: return 'strings:colonistCommon.gameModes.colonistRush.body'
        }
    }

    private static getGameModeName(gameModeData: BaseGameModeData): TranslationObject {
        switch(gameModeData.modeSetting) {
            case ModeSettingEnum.Classic56P: return {key: 'strings:colonistCommon.gameModes.classic56P.abbreviation'}
            case ModeSettingEnum.Classic78P: return {key: 'strings:colonistCommon.gameModes.classic78P.abbreviation'}
            case ModeSettingEnum.Seafarers56P: return {key: 'strings:colonistCommon.gameModes.seafarers56P.abbreviation'}
            case ModeSettingEnum.CitiesAndKnights56P: return {key: 'strings:colonistCommon.gameModes.citiesAndKnights56P.abbreviation'}
            case ModeSettingEnum.CitiesAndKnightsSeafarers4P: return {key: 'strings:colonistCommon.gameModes.seafarersAndCitiesAndKnights4P.abbreviation'}
            case ModeSettingEnum.CitiesAndKnightsSeafarers56P: return {key: 'strings:colonistCommon.gameModes.seafarersAndCitiesAndKnights56P.abbreviation'}
            default: return gameModeData.name
        }
    }
}
